import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { getUser } from "../lib/auth";
import { Pager } from "../components/Pager";
import { SortableTh } from "../components/SortableTh";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAutoPageSize } from "../lib/useAutoPageSize";
import { useSortable } from "../lib/useSortable";
import { usePersistedPage } from "../lib/usePersistedPage";

type ContactRow = {
  id: number;
  first_name: string;
  company: string | null;
  email: string;
  phone: string | null;
  topic: string | null;
  message: string;
  locale: string | null;
  handled: boolean;
  created_at: string;
};

const FILTERS = [
  { v: "",      label: "全部" },
  { v: "false", label: "未处理" },
  { v: "true",  label: "已处理" },
] as const;

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "刚刚";
  if (m < 60) return `${m} 分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时前`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} 天前`;
  return new Date(iso).toLocaleDateString("zh-CN");
}

export function ContactsListPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<string>("");
  const [rows, setRows]     = useState<ContactRow[] | null>(null);
  const [error, setError]   = useState<string | null>(null);
  const [page, setPage]     = usePersistedPage("kexi_contacts_page");
  const [delFor, setDelFor] = useState<ContactRow | null>(null);
  const isAdmin = getUser()?.role === "admin";
  const { containerRef, pageSize } = useAutoPageSize();

  const load = () => {
    setError(null);
    setRows(null);
    const qs = filter ? `?handled=${filter}` : "";
    api.get<ContactRow[]>(`/api/admin/contacts${qs}`).then(setRows).catch((e) => setError(String(e)));
  };

  const doDelete = async () => {
    if (!delFor) return;
    try { await api.delete(`/api/admin/contacts/${delFor.id}`); setRows((rs) => rs?.filter((x) => x.id !== delFor.id) ?? null); }
    catch (e) { setError(String(e)); }
    setDelFor(null);
  };

  useEffect(load, [filter]);
  // 切换筛选回第 1 页：在筛选按钮 onClick 里 setPage(1)（不用监听 effect，StrictMode 下不可靠）

  const { sorted, sortKey, sortDir, toggle } = useSortable(rows ?? []);
  const pageRows = rows ? sorted.slice((page - 1) * pageSize, page * pageSize) : null;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">联系咨询</div>
        <div className="page-actions">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              className={`btn ${filter === f.v ? "btn-primary" : ""}`}
              style={{ height: 30 }}
              onClick={() => { setFilter(f.v); setPage(1); }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {!rows && !error && <div className="muted">加载中…</div>}

      {rows && rows.length === 0 && <div className="muted">暂无记录</div>}

      <div ref={containerRef}>
      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {(() => { const s = { active: sortKey, dir: sortDir, toggle }; return <>
              <SortableTh sortKey="created_at" {...s} style={{ width: 90 }}>时间</SortableTh>
              <SortableTh sortKey="first_name" {...s} style={{ width: 90 }}>姓名</SortableTh>
              <SortableTh sortKey="company" {...s} style={{ width: 150 }}>机构 / 公司</SortableTh>
              <SortableTh sortKey="topic" {...s} style={{ width: 168, whiteSpace: "nowrap" }}>身份</SortableTh>
              <SortableTh sortKey="message" {...s}>留言摘要</SortableTh>
              <SortableTh sortKey="handled" {...s} style={{ width: 90, whiteSpace: "nowrap" }}>状态</SortableTh>
              <th style={{ width: 70 }}></th>
              </>; })()}
            </tr>
          </thead>
          <tbody>
            {pageRows!.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => nav(`/contacts/${r.id}`)}>
                <td className="muted" title={new Date(r.created_at).toLocaleString()}>{timeAgo(r.created_at)}</td>
                <td>{r.first_name}</td>
                <td>{r.company || <span className="muted">—</span>}</td>
                <td style={{ whiteSpace: "nowrap" }}>{r.topic || <span className="muted">—</span>}</td>
                <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.message}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {r.handled
                    ? <span className="muted">已处理</span>
                    : <span className="success">● 未处理</span>}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 6 }}>
                    {isAdmin && <button className="btn btn-xs" style={{ color: "#b04a3a", borderColor: "#b04a3a" }} onClick={() => setDelFor(r)}>删除</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows && <Pager page={page} pageSize={pageSize} total={rows.length} onChange={setPage} />}
      </div>

      <ConfirmDialog
        open={delFor !== null}
        title="删除联系咨询"
        message={delFor ? <>确认删除「{delFor.first_name}」的联系咨询？记录与附件文件将被永久删除，不可恢复。</> : null}
        confirmLabel="删除"
        danger
        onConfirm={doDelete}
        onCancel={() => setDelFor(null)}
      />
    </div>
  );
}
